import * as React from "react";
import ActiveStatusControl from "../../core/ActiveStatusControl/ActiveStatusControl";
import BackButton from "../../core/BackButton/BackButton";
import Checkbox from "../../core/Checkbox/Checkbox";
import ConfirmModal from "../../core/ConfirmModal/ConfirmModal";
import ErrorModal from "../../core/ErrorModal/ErrorModal";
import Spinner from "../../core/Spinner/Spinner";
import Toast from "../../core/Toast/Toast";
import { ITranslationProps, translateComponent } from "../../helpers/translation";
import IResponse from "../../types/IResponse";
import { IUser } from "../../types/IUser";

interface IUserEditProps extends ITranslationProps {
    loading: boolean;
    user: IUser;
    saveUser: (user: IUser) => Promise<IResponse<IUser> | null>;
    sendRegistrationEmail: () => void;
    toggleUserStatus: (active: boolean) => void;
}

interface IUserEditState {
    validated: boolean;
    user: IUser;
    showRegistrationEmailConfirmModal: boolean;
    errorMessages: string[];
    showErrorModal: boolean;
}

class UserEdit extends React.Component<IUserEditProps, IUserEditState> {

    public static getDerivedStateFromProps(nextProps: IUserEditProps, prevState: IUserEditState) {
        prevState.user = nextProps.user;
        return prevState;
    }

    private formElement: HTMLFormElement | null;
    private toastElement: Toast | null;

    constructor(props: IUserEditProps) {
        super(props);

        this.state = {
            validated: false,
            user: {
                email: "",
                firstName: "",
                id: -1,
                isActive: true,
                lastName: "",
                userRoles: []
            },
            showRegistrationEmailConfirmModal: false,
            errorMessages: [],
            showErrorModal: false
        };

        this.onFirstNameChange = this.onFirstNameChange.bind(this);
        this.onLastNameChange = this.onLastNameChange.bind(this);
        this.onEmailChange = this.onEmailChange.bind(this);
        this.onSaveClick = this.onSaveClick.bind(this);
        this.onRoleChecked = this.onRoleChecked.bind(this);
        this.closeRegistrationEmailConfirmModal = this.closeRegistrationEmailConfirmModal.bind(this);
        this.onSendRegistrationEmailClick = this.onSendRegistrationEmailClick.bind(this);
        this.sendRegistrationEmail = this.sendRegistrationEmail.bind(this);
        this.toggleIsActive = this.toggleIsActive.bind(this);
        this.closeErrorModal = this.closeErrorModal.bind(this);
    }

    public render() {
        const roleCheckboxes: React.ReactNode[] = [];
        this.state.user.userRoles.forEach((role, index) => {
            roleCheckboxes.push(
                <Checkbox key={index} onChecked={this.onRoleChecked}
                    checked={role.isActive} value={role.roleID} name={role.roleName} />
            );
        });

        return (
            <div>
                {this.props.loading && <Spinner />}
                <BackButton />
                <h2>{this.props.user.id > 0 ? this.props.t("UserEdit.EditUser") : this.props.t("UserEdit.CreateUser")}</h2>

                <form ref={ref => this.formElement = ref} className={this.state.validated ? "was-validated" : "needs-validation"} noValidate={true}>

                    <div className="card mt-3">
                        <div className="card-header">
                            <h5>{this.props.t("UserEdit.UserInformation")}</h5>
                        </div>
                        <div className="card-body">

                            <div className="form-group">
                                <label>{this.props.t("UserEdit.FirstName")}</label>
                                <input type="text" className="form-control" placeholder={this.props.t("UserEdit.FirstName")}
                                    value={this.state.user.firstName} onChange={this.onFirstNameChange} required={true} maxLength={50} />
                                <div className="invalid-feedback">
                                    {this.props.t("UserEdit.FirstNameInvalid")}
                                </div>
                            </div>

                            <div className="form-group">
                                <label>{this.props.t("UserEdit.LastName")}</label>
                                <input type="text" className="form-control" placeholder={this.props.t("UserEdit.LastName")}
                                    value={this.state.user.lastName} onChange={this.onLastNameChange} required={true} maxLength={50} />
                                <div className="invalid-feedback">
                                    {this.props.t("UserEdit.LastNameInvalid")}
                                </div>
                            </div>

                            <div className="form-group">
                                <label>{this.props.t("UserEdit.Email")}</label>
                                <input type="email" className="form-control" placeholder={this.props.t("UserEdit.Email")}
                                    value={this.state.user.email} onChange={this.onEmailChange} required={true} maxLength={50} />
                                <div className="invalid-feedback">
                                    {this.props.t("UserEdit.EmailInvalid")}
                                </div>
                            </div>

                            {this.props.user.id > 0 &&
                                <div>
                                    <ActiveStatusControl
                                        isActive={this.state.user.isActive}
                                        label={this.props.t("UserEdit.Status")}
                                        activateConfirmationMessage={this.props.t("UserEdit.ConfirmActivate")}
                                        inactivateConfirmationMessage={this.props.t("UserEdit.ConfirmInactivate")}
                                        onConfirmation={this.toggleIsActive} />

                                    <div className="form-group">
                                        <a href="#" onClick={this.onSendRegistrationEmailClick}>{this.props.t("UserEdit.SendRegistrationEmail")}</a>
                                    </div>
                                </div>
                            }
                        </div>
                    </div>

                    <div className="card mt-3">

                        <div className="card-header">
                            <h5>{this.props.t("UserEdit.Roles")}</h5>
                        </div>
                        <div className="card-body">
                            {roleCheckboxes}
                        </div>
                    </div>

                    <button type="button" data-id="save-button" className="btn btn-primary mt-3 px-4"
                        onClick={this.onSaveClick}>{this.props.t("Global.SaveButton")}</button>
                </form>

                <ConfirmModal
                    onRequestClose={this.closeRegistrationEmailConfirmModal}
                    onConfirmation={this.sendRegistrationEmail}
                    isOpen={this.state.showRegistrationEmailConfirmModal}
                    title={this.props.t("Global.Confirm")}
                    message={this.props.t("UserEdit.ConfirmRegistrationEmail")} />

                <ErrorModal
                    title={this.props.t("Global.Error")}
                    errors={this.state.errorMessages}
                    isOpen={this.state.showErrorModal}
                    onRequestClose={this.closeErrorModal} />

                <Toast ref={ref => this.toastElement = ref} />
            </div>
        );
    }

    private closeRegistrationEmailConfirmModal() {
        this.setState({ showRegistrationEmailConfirmModal: false });
    }

    private closeErrorModal() {
        this.setState({ showErrorModal: false, errorMessages: [] });
    }

    private async onSaveClick() {

        this.setState({ validated: true });

        if (this.formElement && this.formElement.checkValidity()) {

            const response = await this.props.saveUser(this.state.user);
            if (response) {
                if (response.success) {
                    if (this.toastElement) {
                        this.toastElement.success(this.props.t("Global.Success"));
                    }
                }
                else {
                    this.setState({ showErrorModal: true, errorMessages: response.errorMessages })
                }
            }

            this.setState({ validated: false });
        }
    }

    private onRoleChecked(value: string | number, checked: boolean) {
        const user = this.state.user;
        const role = user.userRoles.find(r => r.roleID === value);
        if (role) {
            role.isActive = checked;
            this.setState({ user });
        }
    }

    private onFirstNameChange(event: React.ChangeEvent<HTMLInputElement>) {
        this.userFieldUpdate("firstName", event.target.value);
    }

    private onLastNameChange(event: React.ChangeEvent<HTMLInputElement>) {
        this.userFieldUpdate("lastName", event.target.value);
    }

    private onEmailChange(event: React.ChangeEvent<HTMLInputElement>) {
        this.userFieldUpdate("email", event.target.value);
    }

    private userFieldUpdate(fieldName: string, newValue: string) {
        const user = this.state.user;
        user[fieldName] = newValue;
        this.setState({ user });
    }

    private onSendRegistrationEmailClick(event: React.MouseEvent<HTMLAnchorElement>) {
        event.preventDefault();
        this.setState({ showRegistrationEmailConfirmModal: true });
    }

    private async toggleIsActive() {
        await this.props.toggleUserStatus(!this.state.user.isActive);
        if (this.toastElement) {
            this.toastElement.success(this.props.t("Global.Success"), "");
        }

    }

    private async sendRegistrationEmail() {

        await this.props.sendRegistrationEmail();
        if (this.toastElement) {
            this.toastElement.success(this.props.t("Global.Success"));
        }
    }
}

export default translateComponent(UserEdit);



// WEBPACK FOOTER //
// ./src/components/UserEdit/UserEdit.tsx